// Where trees are derived and digests are signed. The signer (signer.ts)
// never touches a secret itself: it asks a SigningBackend for a tree's
// verifying key and for signatures, and tells it which trees to forget.
// Two implementations:
//
// * InThreadBackend holds the master (k, c) in the calling thread. Node
//   tools, tests and the M6 canary use it; it behaves exactly as the signer
//   did before the backend existed.
// * WorkerBackend forwards to a Web Worker through WorkerClient. The worker
//   holds the master (it opened the sealed envelope itself, worker.ts
//   'unseal') and every tree's 212 KB of secrets; the main thread, and so
//   the app's UI, only ever sees public keys and signatures
//   (docs/08-app-design.md section 7).
//
// Both are deterministic in the same way: the same (tree, leaf, digest)
// yields the same signature bytes on every call, which the ledger's resume
// path relies on (docs/01 section 10).

import type { SecretKey } from '../vendor/solana-winternitz/src/hazmat/xmss.js';
import { type Master, type TreeSecrets, deriveTree, signDeterministic, treeKey, wipe } from './derive.js';
import type { WorkerClient } from './worker-client.js';

export interface SigningBackend {
  /** Derive tree `treeIndex` (idempotent: a tree already held is not derived again) and return its 41-byte verifying key. */
  deriveTree(treeIndex: number): Promise<Uint8Array>;
  /** Sign `digest` at `leaf` of a tree this backend holds; identical bytes on every call. */
  sign(treeIndex: number, leaf: number, digest: Uint8Array): Promise<{ signature: Uint8Array; saltTrial: number }>;
  /** Zero and forget one tree's secrets; a no-op for a tree not held. */
  wipeTree(treeIndex: number): Promise<void>;
  /** Zero and forget every tree; the master, where the backend holds one, survives (locking the app wipes that separately). */
  wipeTrees(): Promise<void>;
}

interface Held {
  readonly ts: TreeSecrets;
  readonly key: SecretKey;
}

/** The master in the calling thread; trees cached until wiped. */
export class InThreadBackend implements SigningBackend {
  private readonly master: Master;
  private readonly programId: Uint8Array;
  private readonly trees = new Map<number, Held>();

  constructor(master: Master, programId: Uint8Array) {
    this.master = master;
    this.programId = programId;
  }

  async deriveTree(treeIndex: number): Promise<Uint8Array> {
    let held = this.trees.get(treeIndex);
    if (!held) {
      const ts = deriveTree(this.master, this.programId, treeIndex);
      held = { ts, key: treeKey(ts) };
      this.trees.set(treeIndex, held);
    }
    return held.key.verifyingKey().toBytes();
  }

  async sign(treeIndex: number, leaf: number, digest: Uint8Array): Promise<{ signature: Uint8Array; saltTrial: number }> {
    const held = this.trees.get(treeIndex);
    if (!held) throw new Error(`tree ${treeIndex} is not held by this backend; derive it first`);
    const { signature, saltTrial } = signDeterministic(held.key, held.ts.seed, leaf, digest);
    return { signature: signature.toBytes(), saltTrial };
  }

  async wipeTree(treeIndex: number): Promise<void> {
    const held = this.trees.get(treeIndex);
    if (!held) return;
    wipe(held.ts.secrets, held.ts.seed);
    this.trees.delete(treeIndex);
  }

  async wipeTrees(): Promise<void> {
    for (const held of this.trees.values()) wipe(held.ts.secrets, held.ts.seed);
    this.trees.clear();
  }

  /** Zero the master too; the backend is unusable afterwards. The app's lock path. */
  async wipeAll(): Promise<void> {
    await this.wipeTrees();
    wipe(this.master.k, this.master.c);
  }
}

/**
 * Trees and signatures from a worker that already holds the master (after
 * `WorkerClient.unseal`). Tracks which trees it asked for so `wipeTrees`
 * can forget them one by one without wiping the master.
 */
export class WorkerBackend implements SigningBackend {
  private readonly client: WorkerClient;
  private readonly programId: Uint8Array;
  private readonly keys = new Map<number, Uint8Array>();

  constructor(client: WorkerClient, programId: Uint8Array) {
    this.client = client;
    this.programId = programId;
  }

  async deriveTree(treeIndex: number): Promise<Uint8Array> {
    const cached = this.keys.get(treeIndex);
    if (cached) return cached;
    const { verifyingKey } = await this.client.deriveHeldTree(this.programId, treeIndex);
    this.keys.set(treeIndex, verifyingKey);
    return verifyingKey;
  }

  async sign(treeIndex: number, leaf: number, digest: Uint8Array): Promise<{ signature: Uint8Array; saltTrial: number }> {
    const { signature, saltTrial } = await this.client.sign(treeIndex, leaf, digest);
    return { signature, saltTrial };
  }

  async wipeTree(treeIndex: number): Promise<void> {
    if (!this.keys.has(treeIndex)) return;
    await this.client.wipeTree(treeIndex);
    this.keys.delete(treeIndex);
  }

  async wipeTrees(): Promise<void> {
    for (const treeIndex of [...this.keys.keys()]) await this.wipeTree(treeIndex);
  }
}
